import React, { useEffect, useRef, useState } from "react";
import { BetDetails } from "../../../../models/MyBetModel.tsx";
import { Odd } from "../../../../models/Odd_response_model.tsx";
import { useBetContext } from "../../../../context/BetContext.tsx";
import { calcStake, formatNumber } from "../../../../helpers/functions.tsx";

export function SelectedOddDisplay({
    o,
    onRequestBetsModal,
    currentBet,
    isFuture = false,
    propsLive = false,
    disabled = false,
    disabledReason,
}: {
    o: Odd;
    onRequestBetsModal: Function;
    currentBet?: BetDetails;
    isFuture?: boolean;
    isSpecial?: boolean;
    propsLive?: boolean;
    disabled?: boolean;
    disabledReason?: string;
}) {
    const [reward, setReward] = useState<string>(currentBet?.reward ? String(currentBet.reward) : '');
    const { deleteBet, updateBet } = useBetContext();
    const isSubmittingRef = useRef(false);
    const [priceChange, setPriceChange] = useState<'up' | 'down' | ''>('');
    const prevOddRef = useRef({
        price: o.price,
        points: o.points
    });
    const isSelected = currentBet?.id_selection === o.id;
    const isBlocked = Boolean(disabled) && !isSelected;

    function updateProfit(value: string) {
        if (isBlocked) return;
        setReward(value);

        const parsed = parseFloat(value);

        if (!value || isNaN(parsed)) {
            deleteBet(o.id);
            return;
        }

        if (currentBet) {
            const updatedBet: BetDetails = {
                ...currentBet,
                bet: calcStake(parsed, o.price),
                reward: parsed
            };
            updateBet(updatedBet);
        }
    }

    useEffect(() => {
        setReward(
            o.price > 0
                ? (currentBet?.bet ? String(currentBet.bet) : '')
                : (currentBet?.reward ? String(currentBet.reward) : '')
        );
    }, [currentBet?.bet, currentBet?.reward, o.price]);

    function processBet(value: string) {
        if (isBlocked) return;
        const parsed = parseFloat(value);

        if (!value || isNaN(parsed) || parsed <= 0) {
            deleteBet(o.id);
            return;
        }

        onRequestBetsModal({
            ...currentBet,
            id_selection: o.id,
            betName: o.name,
            odd: o,
            reward: parsed
        }, false);
    }

    useEffect(() => {
        if (!propsLive) return;

        const prev = prevOddRef.current;
        const changed = prev.price !== o.price || prev.points !== o.points;

        if (changed) {
            let direction: 'up' | 'down';

            if (o.price !== prev.price) {
                direction = o.price > prev.price ? 'up' : 'down';
            } else {
                direction = o.points > prev.points ? 'up' : 'down';
            }

            setPriceChange(direction);
            prevOddRef.current = {
                price: o.price,
                points: o.points
            };

            const timeout = setTimeout(() => {
                setPriceChange('');
            }, 2000);

            return () => clearTimeout(timeout);
        }
    }, [o.price, o.points, propsLive]);

    const priceClass = propsLive ? priceChange === 'up' ? 'price-up' : priceChange === 'down' ? 'price-down' : '' : '';

    return (
        <>
            <div className={`${isFuture ? "odd-actions" : "large-screen"} ${o.selection_line ?? ''} ${isBlocked ? 'props-odd-disabled' : ''}`}>
                <div className="future-container-name">
                    {o && (
                        <label
                            className={`select-box ${priceClass} ${isBlocked ? 'props-odd-disabled' : ''}`}
                            title={isBlocked ? disabledReason : undefined}
                            onClick={(e) => {
                                e.preventDefault();
                                if (isBlocked) return;
                                onRequestBetsModal({
                                    ...currentBet,
                                    id_selection: o.id,
                                    betName: o.name,
                                    odd: o,
                                    reward: 0,
                                }, true);
                            }}
                        >
                            {isFuture
                                ? formatNumber(o.price)
                                : `${formatNumber(o.points, o.selection_line, "points")} ${formatNumber(o.price)}`
                            }
                        </label>
                    )}
                </div>

                <div className="input-container-bet ">
                    <input
                        type="text"
                        className="textbox"
                        value={reward}
                        disabled={isBlocked}
                        title={isBlocked ? disabledReason : undefined}
                        onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                                updateProfit(value);
                            }
                        }}
                        onBlur={(e) => {
                            if (isSubmittingRef.current) {
                                isSubmittingRef.current = false;
                                return;
                            }
                            processBet(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (isBlocked) return;
                            if (e.key === 'Enter') {
                                const parsed = parseFloat(reward);

                                if (!isNaN(parsed)) {
                                    onRequestBetsModal({
                                        ...currentBet,
                                        id_selection: o.id,
                                        betName: o.name,
                                        odd: o,
                                        reward: parsed
                                    }, true);
                                }
                            }
                        }}
                    />
                </div>
            </div>

            <label
                className={`${isFuture ? 'combined-price' : 'small-screen'} ${isSelected ? 'selected' : ''} ${isBlocked ? 'props-odd-disabled' : ''} ${o.market_id === 'total' ? 'total-odd' : ''} ${priceClass}`}
                title={isBlocked ? disabledReason : undefined}
                aria-disabled={isBlocked}
                onClick={(e) => {
                    e.preventDefault();
                    if (isBlocked) {
                        return;
                    }
                    if (isSelected) {
                        deleteBet(o.id);
                        return;
                    }
                    onRequestBetsModal({
                        ...currentBet,
                        id_selection: o.id,
                        betName: o.name,
                        odd: o,
                        reward: 0,
                    }, true);
                }}
            >
                <span className="points" style={{ fontSize: "14px" }}>
                    {isFuture
                        ? formatNumber(o.price)
                        : formatNumber(o.points, o.selection_line, "points")}
                </span>

                {!isFuture && (
                    <span style={{ fontSize: "14px" }}>
                        {propsLive ? formatNumber(o.price) : `\u00A0${formatNumber(o.price)}`}
                    </span>
                )}
            </label>
        </>
    );
}
